import { S3_BUCKET_TIMEOUT } from "constants/S3Constant";

export const uploadFileToS3 = (
  file,
  uploadUrl,
  onProgress,
  onXhrCreated,
  cancelToken
) => {
  const startedAt = Date.now();
  const fail = (msg, httpStatus) => {
    const err = new Error(msg);
    err.durationMs = Date.now() - startedAt;
    err.httpStatus = httpStatus ?? null;
    return err;
  };
  return new Promise((resolve, reject) => {
    if (!uploadUrl) {
      reject(new Error("Upload URL is missing"));
      return;
    }

    // Check if already cancelled before starting
    if (cancelToken && cancelToken.isCancelled) {
      reject(new Error("Upload cancelled before starting"));
      return;
    }

    const xhr = new XMLHttpRequest();

    // Store reference for cancellation
    if (onXhrCreated) {
      onXhrCreated(xhr);
    }

    xhr.open("PUT", uploadUrl, true);
    xhr.setRequestHeader("Content-Type", file.type);

    // Enhanced cancellation check with immediate abort
    const checkCancellation = () => {
      if (cancelToken && cancelToken.isCancelled) {
        if (xhr.readyState !== XMLHttpRequest.DONE) {
          xhr.abort();
        }
        reject(new Error("Upload cancelled by user"));
        return true;
      }
      return false;
    };

    xhr.upload.onprogress = (event) => {
      if (checkCancellation()) return;

      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        if (onProgress && !checkCancellation()) {
          onProgress(progress);
        }
      }
    };

    xhr.onload = () => {
      if (checkCancellation()) return;

      if (xhr.status === 200) {
        let cfRay = null;
        try { cfRay = xhr.getResponseHeader("cf-ray"); } catch (e) { cfRay = null; }
        resolve({
          success: true,
          fileName: file.name,
          status: xhr.status,
          statusText: xhr.statusText,
          etag: xhr.getResponseHeader("ETag"),
          durationMs: Date.now() - startedAt,
          cfRay,
        });
      } else {
        reject(fail(`Upload failed: ${xhr.status} ${xhr.statusText}`, xhr.status));
      }
    };

    xhr.onerror = () => {
      if (!checkCancellation()) {
        reject(fail("Network error during upload", 0));
      }
    };

    xhr.ontimeout = () => {
      if (!checkCancellation()) {
        reject(fail("Upload timeout", 0));
      }
    };

    xhr.onabort = () => {
      reject(new Error("Upload cancelled by user"));
    };

    // Set timeout
    xhr.timeout = S3_BUCKET_TIMEOUT;

    // Final cancellation check before sending
    if (checkCancellation()) {
      return;
    }

    try {
      xhr.send(file);
    } catch (error) {
      reject(new Error(`Failed to send file: ${error.message}`));
    }
  });
};
